
import { Component } from '@theme/component';
import { oncePerEditorSession } from '@theme/utilities';

/**
 * Comparison slider component for comparing two images.
 * Supports native range input and touch dragging on iOS Safari.
 *
 * @typedef {object} ComparisonSliderRefs
 * @property {HTMLElement} mediaWrapper
 * @property {HTMLInputElement} slider
 * @property {HTMLElement} afterImage
 * @extends {Component<ComparisonSliderRefs>}
 */
export class ComparisonSliderComponent extends Component {
  requiredRefs = ['mediaWrapper', 'slider', 'afterImage'];

  constructor() {
    super();
    this.hasAnimated = false;
    this.isAnimating = false;
    this.activePointerId = null;
    this.animationTimers = [];
    this.boundHandleIntersection = this.handleIntersection.bind(this);
    this.boundPointerDown = this.handlePointerDown.bind(this);
    this.boundPointerMove = this.handlePointerMove.bind(this);
    this.boundPointerUp = this.handlePointerUp.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    const { mediaWrapper, slider } = this.refs;
    if (!mediaWrapper || !slider) return;

    this.orientation = mediaWrapper.dataset.orientation || 'horizontal';
    this.sync();

    mediaWrapper.style.touchAction =
      this.orientation === 'horizontal' ? 'pan-y' : 'pan-x';

    slider.style.touchAction =
      this.orientation === 'horizontal' ? 'pan-y' : 'pan-x';

    mediaWrapper.addEventListener('pointerdown', this.boundPointerDown, true);
    mediaWrapper.addEventListener('pointermove', this.boundPointerMove, true);
    mediaWrapper.addEventListener('pointerup', this.boundPointerUp, true);
    mediaWrapper.addEventListener('pointercancel', this.boundPointerUp, true);

    this.setupIntersectionObserver();
  }

  disconnectedCallback() {
    const { mediaWrapper } = this.refs;

    if (mediaWrapper) {
      mediaWrapper.removeEventListener('pointerdown', this.boundPointerDown, true);
      mediaWrapper.removeEventListener('pointermove', this.boundPointerMove, true);
      mediaWrapper.removeEventListener('pointerup', this.boundPointerUp, true);
      mediaWrapper.removeEventListener('pointercancel', this.boundPointerUp, true);
    }

    this.activePointerId = null;
    this.stopAnimation();

    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    super.disconnectedCallback();
  }

  sync() {
    const { mediaWrapper, slider } = this.refs;
    if (!mediaWrapper || !slider || this.isAnimating) return;

    const min = Number(slider.min || 0);
    const max = Number(slider.max || 100);
    const denominator = max - min;

    if (denominator <= 0) return;

    const fraction = (Number(slider.value) - min) / denominator;
    mediaWrapper.style.setProperty(
      '--compare',
      String(Math.round(fraction * 100))
    );
  }

  setValue(value) {
    const { slider } = this.refs;
    if (!slider) return;

    const min = Number(slider.min || 0);
    const max = Number(slider.max || 100);
    const next = Math.min(max, Math.max(min, Number(value)));

    if (!Number.isFinite(next)) return;

    slider.value = String(next);
    this.sync();
  }

  stopAnimation() {
    this.animationTimers.forEach((id) => clearTimeout(id));
    this.animationTimers = [];
    this.isAnimating = false;

    const { mediaWrapper } = this.refs;
    if (mediaWrapper) {
      mediaWrapper.style.setProperty('--transition-duration', '0s');
    }
  }

  updateFromPointer(event) {
    const { mediaWrapper, slider } = this.refs;
    if (!mediaWrapper || !slider) return;

    const rect = mediaWrapper.getBoundingClientRect();
    const horizontal = this.orientation === 'horizontal';
    const length = horizontal ? rect.width : rect.height;

    if (length <= 0) return;

    const position = horizontal
      ? (event.clientX - rect.left) / length
      : (event.clientY - rect.top) / length;

    const fraction = Math.max(0, Math.min(1, position));

    const min = Number(slider.min || 0);
    const max = Number(slider.max || 100);
    const step = Number(slider.step || 1);

    let value = min + fraction * (max - min);

    if (Number.isFinite(step) && step > 0) {
      value = min + Math.round((value - min) / step) * step;
    }

    value = Math.max(min, Math.min(max, value));
    slider.value = String(value);

    this.sync();

    slider.dispatchEvent(
      new Event('input', { bubbles: true })
    );
  }

  handlePointerDown(event) {
    if (event.pointerType !== 'touch' &&
        event.pointerType !== 'pen') return;

    if (this.activePointerId !== null) return;

    const { mediaWrapper } = this.refs;
    if (!mediaWrapper) return;

    this.stopAnimation();
    this.hasAnimated = true;
    this.activePointerId = event.pointerId;

    try {
      mediaWrapper.setPointerCapture(event.pointerId);
    } catch (_) {
      // Safari fallback
    }

    this.updateFromPointer(event);
  }

  handlePointerMove(event) {
    if (event.pointerId !== this.activePointerId) return;
    this.updateFromPointer(event);
  }

  handlePointerUp(event) {
    if (event.pointerId !== this.activePointerId) return;

    const { mediaWrapper } = this.refs;

    if (event.type === 'pointerup') {
      this.updateFromPointer(event);
    }

    if (mediaWrapper &&
        mediaWrapper.hasPointerCapture?.(event.pointerId)) {
      try {
        mediaWrapper.releasePointerCapture(event.pointerId);
      } catch (_) {
        // Ignore
      }
    }

    this.activePointerId = null;
  }

  animateSlider() {
    const { mediaWrapper, slider } = this.refs;
    if (!mediaWrapper || !slider || this.hasAnimated) return;

    this.hasAnimated = true;
    this.isAnimating = true;

    mediaWrapper.style.setProperty('--transition-duration', '0.5s');

    this.animationTimers.push(setTimeout(() => {
      if (this.isAnimating) {
        mediaWrapper.style.setProperty('--compare', '40');
      }
    }, 100));

    this.animationTimers.push(setTimeout(() => {
      if (this.isAnimating) {
        mediaWrapper.style.setProperty('--compare', '60');
      }
    }, 600));

    this.animationTimers.push(setTimeout(() => {
      if (this.isAnimating) {
        mediaWrapper.style.setProperty('--compare', '50');
      }
    }, 1100));

    this.animationTimers.push(setTimeout(() => {
      if (!this.isAnimating) return;

      mediaWrapper.style.setProperty('--transition-duration', '0s');
      slider.value = '50';
      this.isAnimating = false;
      this.animationTimers = [];
      this.sync();
    }, 1600));
  }

  setupIntersectionObserver() {
    if (!window.IntersectionObserver) return;

    this.intersectionObserver = new IntersectionObserver(
      this.boundHandleIntersection,
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.5
      }
    );

    this.intersectionObserver.observe(this);
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !this.hasAnimated) {
        this.animationTimers.push(setTimeout(() => {
          if (this.isConnected && !this.hasAnimated) {
            oncePerEditorSession(
              this,
              'comparison-slider-animated',
              () => this.animateSlider()
            );
          }
        }, 300));

        this.intersectionObserver?.disconnect();
      }
    });
  }
}

if (!customElements.get('comparison-slider-component')) {
  customElements.define(
    'comparison-slider-component',
    ComparisonSliderComponent
  );
}
